import { useCallback, useEffect, useState } from "react";

export interface WorkflowSummary {
  id: string;
  name: string;
  isEnabled?: boolean;
  flowType?: string;
  objectTypeId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  startActionId?: string;
  actions?: { actionId: string; [key: string]: unknown }[];
  enrollmentCriteria?: Record<string, unknown>;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const b = body as { error?: string; message?: string };
    const msg = b.error ?? b.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export function useWorkflowList(portalId: string | null) {
  const [items, setItems] = useState<WorkflowSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!portalId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getJson<{ results?: WorkflowSummary[] }>(
        `/api/workflows/${portalId}`,
      );
      setItems(data.results ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { items, loading, error, reload };
}

export function useWorkflow(portalId: string | null, flowId: string | null) {
  const [definition, setDefinition] = useState<WorkflowDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!portalId || !flowId) return;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await getJson<WorkflowDefinition>(
          `/api/workflows/${portalId}/${flowId}`,
        );
        if (!cancelled) setDefinition(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [portalId, flowId]);

  return { definition, loading, error };
}
